import { useEffect, useState } from 'react';
/**
 * Returns the state of a promise as React state, using the same format as `Promise.allSettled`.
 * @param promise The promise to await.
 * @returns Promise settled result representing the current state of the promise.
 * - `undefined`: the Promise is still pending, no result
 * - `{status: 'fulfilled', value}`: the Promise has resolved, and the value is available
 * - `{status: 'rejected', reason}`: the Promise has rejected, but the error reason is available
 * @example
 * const result = useSettledPromise(networkResponse);
 * switch (result?.status) {
 *   case 'fulfilled':
 *     return <span>{result.value}</span>;
 *   case 'rejected':
 *     return <p class="error">{result.reason.message}</p>;
 *   case undefined:
 *     return <LoadingSpinner />;
 * }
 */
export function useSettledPromise(promise) {
    const [settled, setSettled] = useState();
    useEffect(() => {
        setSettled(undefined);
        if (!promise) {
            return undefined;
        }
        // Track if the component is still mounted.
        let cancelled = false;
        promise.then((value) => {
            // Don't set state if the component is unmounted.
            if (!cancelled) {
                setSettled({ status: 'fulfilled', value, promise });
            }
        }, (reason) => {
            if (!cancelled) {
                setSettled({ status: 'rejected', reason, promise });
            }
        });
        return () => {
            cancelled = true;
        };
    }, [promise]);
    if (settled?.promise !== promise) {
        // The useEffect above runs asynchronously after this returns the value. It properly clears settled if the
        // promise changes but not until after this stale value is returned. If the saved settled value is from a
        // previous promise then don't return it.
        return undefined;
    }
    else {
        return settled;
    }
}
/**
 * Returns a fulfilled promise's value, or undefined if the settled promise is rejected.
 */
export function fulfilled(settled) {
    if (settled?.status === 'fulfilled') {
        return settled.value;
    }
    else {
        return undefined;
    }
}
/**
 * Returns a rejected promise's error reason, or undefined if the settled promise is fulfilled.
 */
export function rejected(settled) {
    if (settled?.status === 'rejected') {
        return settled.reason;
    }
    else {
        return undefined;
    }
}
//# sourceMappingURL=useSettledPromise.js.map